import { Icon } from '../../../../components/icons/Icon'
import { InlineAlert } from '../../../../components/InlineAlert'
import { HelpBalloon } from '../../../../help/HelpBalloon'
import { FilterChipBar } from '../../filter/FilterChipBar'
import { WIDGET_KEY } from '../../widgets/types'
import { WidgetErrorBoundary } from '../../widgets/WidgetErrorBoundary'
import { AllocationUtilizationWidget } from './AllocationUtilizationWidget'
import { JurisdictionHeatmapWidget } from './JurisdictionHeatmapWidget'
import { ProgramRateCompareWidget } from './ProgramRateCompareWidget'
import { RndFilmSplitWidget } from './RndFilmSplitWidget'
import { DashboardThemePicker } from '../../../../theme/DashboardThemePicker'
import type { UseJurisdictionAnalyticsResult } from './useJurisdictionAnalytics'
import type { DashboardFilter } from '../../../../types/insights'
import '../../charts/charts.css'

const AXIS_LABELS: Record<string, string> = {
  jurisdiction: 'Jurisdiction',
  programId: 'Program',
  fiscalYearId: 'Fiscal year',
  kind: 'Kind',
}

/**
 * IB-282 View 3 — presentational grid of `WidgetFrame`-wrapped tiles, each in its own
 * `WidgetErrorBoundary` (double isolation — one tile's failure never blanks the view, mirroring
 * `DashboardScreen`'s exact composition). Pure props-in, `useJurisdictionAnalytics` owns all state.
 */
export function JurisdictionAnalyticsPanel(props: UseJurisdictionAnalyticsResult) {
  const { state, dispatch, unknownKeys, noticeDismissed, dismissNotice, filterFieldsOpen, setFilterFieldsOpen, widgetProps } = props

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--iw-space-3)',
          flexWrap: 'wrap',
          padding: 'var(--iw-space-4) var(--iw-space-5)',
          borderBottom: '1px solid var(--iw-border)',
          background: 'var(--iw-surface)',
        }}
      >
        <span style={{ color: 'var(--iw-accent)', display: 'inline-flex' }}>
          <Icon name="map-pin" size={20} />
        </span>
        <h1 style={{ fontSize: 'var(--iw-size-lg)', margin: 0 }}>Jurisdiction &amp; program analytics</h1>
        <HelpBalloon id="list.insights" />
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="iw-btn iw-btn--sm"
          aria-expanded={filterFieldsOpen}
          onClick={() => setFilterFieldsOpen(!filterFieldsOpen)}
        >
          <Icon name="filter" size={14} />
          Filters
        </button>
        <DashboardThemePicker />
      </div>

      {filterFieldsOpen ? <JurisdictionFilterFields state={state} dispatch={dispatch} /> : null}

      {unknownKeys.length > 0 && !noticeDismissed ? (
        <div style={{ padding: '0 var(--iw-space-5)', paddingTop: 'var(--iw-space-3)' }}>
          <InlineAlert
            severity="warning"
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={dismissNotice}>
                Dismiss
              </button>
            }
          >
            Some filters in this link could not be applied and were ignored.
          </InlineAlert>
        </div>
      ) : null}

      <FilterChipBar state={state} dispatch={dispatch} axisLabels={AXIS_LABELS} />

      <div className="iw-dashboard-grid">
        <WidgetErrorBoundary title="Credit value by state × FY" icon="map-pin">
          <JurisdictionHeatmapWidget {...widgetProps(WIDGET_KEY.creditValueByJurisdictionFy)} />
        </WidgetErrorBoundary>
        <WidgetErrorBoundary title="Program base vs. uplift rate" icon="bar-chart">
          <ProgramRateCompareWidget {...widgetProps(WIDGET_KEY.programRateComparison)} />
        </WidgetErrorBoundary>
        <WidgetErrorBoundary title="Cap / allocation utilization" icon="landmark">
          <AllocationUtilizationWidget {...widgetProps(WIDGET_KEY.allocationPoolUtilization)} />
        </WidgetErrorBoundary>
        <WidgetErrorBoundary title="R&D vs Film split" icon="dollar-sign">
          <RndFilmSplitWidget {...widgetProps(WIDGET_KEY.filingsByStatus)} />
        </WidgetErrorBoundary>
      </div>
    </div>
  )
}

interface JurisdictionFilterFieldsProps {
  state: UseJurisdictionAnalyticsResult['state']
  dispatch: UseJurisdictionAnalyticsResult['dispatch']
}

/**
 * Freeform refinement inputs — same disclosed "raw-ID input, no lookup picker yet" posture as
 * `DashboardScreen`'s own `DashboardFilterFields` (no jurisdiction/program/fiscal-year picker
 * endpoint exists yet). AC3.2/AC3.3 both need `jurisdiction`/`programId`/`fiscalYearId` explicitly
 * chosen — every field dispatches `SET_FILTER` and is fully wired into the URL/cross-filter/
 * AND-compose machinery `useDashboardUrlState` already provides.
 */
function JurisdictionFilterFields({ state, dispatch }: JurisdictionFilterFieldsProps) {
  const fields: Array<{ axis: keyof typeof AXIS_LABELS; placeholder: string }> = [
    { axis: 'jurisdiction', placeholder: 'Jurisdiction code (e.g. US-GA)' },
    { axis: 'programId', placeholder: 'Program ID' },
    { axis: 'fiscalYearId', placeholder: 'Fiscal year ID' },
  ]
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--iw-space-3)',
        padding: 'var(--iw-space-3) var(--iw-space-5)',
        borderBottom: '1px solid var(--iw-border)',
      }}
    >
      {fields.map((f) => (
        <div className="iw-field" key={f.axis}>
          <label className="iw-field-label" htmlFor={`jurisdiction-analytics-filter-${f.axis}`}>
            {AXIS_LABELS[f.axis]}
          </label>
          <input
            id={`jurisdiction-analytics-filter-${f.axis}`}
            className="iw-input"
            defaultValue={state.filters[f.axis as keyof DashboardFilter] ?? ''}
            placeholder={f.placeholder}
            onBlur={(e) =>
              dispatch({ type: 'SET_FILTER', axis: f.axis as keyof DashboardFilter, value: e.target.value.trim() || undefined })
            }
          />
        </div>
      ))}
    </div>
  )
}
